import { Fragment } from 'react';
import { InsightEvent, RunBand } from '../../types/insights';
import { nameToColor } from '../PCTile';
import EventMarkerRow from './EventMarkerRow';

// ---------------------------------------------------------------------------
// RunSceneStrip — one PC's lane on the shared time axis:
//   * 26 px band lane: experience run bands (color = stable hash of the
//     experience name, same helper as the PC tiles), scene sub-ticks inside
//     each run, hatched overlay for labeled test-run windows.
//   * an EventMarkerRow of DOM glyphs underneath.
// All positioning is absolute with % offsets from the visible range, so the
// strip lines up with the uPlot panes (whose x scale is pinned to the same
// range).
// ---------------------------------------------------------------------------

interface RunSceneStripProps {
  label: string;
  runs: RunBand[];
  /** Room-wide test_run windows (hatched overlay across the lane). */
  testWindows: InsightEvent[];
  /** Markers for this lane (already filtered to PC + visible categories). */
  events: InsightEvent[];
  range: [number, number];
  onSelectEvent: (event: InsightEvent) => void;
  selectedEventId?: string | null;
}

const fmtTime = (ts: number) =>
  new Date(ts * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

export default function RunSceneStrip({
  label,
  runs,
  testWindows,
  events,
  range,
  onSelectEvent,
  selectedEventId,
}: RunSceneStripProps) {
  const [from, to] = range;
  const span = Math.max(to - from, 1);
  const nowSec = Date.now() / 1000;

  const pct = (ts: number) => Math.min(Math.max(((ts - from) / span) * 100, 0), 100);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '96px 1fr', gap: 8, alignItems: 'start' }}>
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: '#54626f',
          lineHeight: '26px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={label}
      >
        {label}
      </div>

      <div>
        {/* Band lane */}
        <div
          style={{
            position: 'relative',
            height: 26,
            background: '#f6f8fb',
            border: '1px solid #eef1f5',
            borderRadius: 6,
            overflow: 'hidden',
          }}
        >
          {runs.map((run) => {
            const start = Math.max(run.startTs, from);
            const end = Math.min(run.endTs ?? Math.min(nowSec, to), to);
            if (end <= start) return null;
            const color = nameToColor(run.experienceName);
            const title =
              `${run.experienceName}${run.experienceVersion ? ` v${run.experienceVersion}` : ''}` +
              ` · ${fmtTime(run.startTs)}–${run.endTs ? fmtTime(run.endTs) : '…'}` +
              (run.language ? ` · ${run.language}` : '');
            return (
              <Fragment key={run.runId}>
                <div
                  title={title}
                  style={{
                    position: 'absolute',
                    left: `${pct(start)}%`,
                    width: `${Math.max(pct(end) - pct(start), 0.15)}%`,
                    top: 2,
                    bottom: 2,
                    background: color.bg,
                    border: `1px solid ${color.border}`,
                    borderRadius: 4,
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    fontSize: 10,
                    lineHeight: '20px',
                    paddingLeft: 4,
                    color: '#54626f',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {run.experienceName}
                </div>
                {/* Scene sub-ticks (skip a tick sitting on the run start). */}
                {run.scenes.map((scene, i) => {
                  if (scene.startTs <= start + 1 || scene.startTs >= end) return null;
                  return (
                    <div
                      key={`${run.runId}-s${i}`}
                      title={`${scene.sceneName ?? `Scene ${scene.sceneIndex ?? i}`} · ${fmtTime(scene.startTs)}`}
                      style={{
                        position: 'absolute',
                        left: `${pct(scene.startTs)}%`,
                        top: 2,
                        bottom: 2,
                        width: 1,
                        background: color.border,
                        opacity: 0.75,
                      }}
                    />
                  );
                })}
              </Fragment>
            );
          })}

          {/* Test-run hatched overlays */}
          {testWindows.map((tw) => {
            const start = Math.max(tw.ts, from);
            const end = Math.min(tw.endTs ?? Math.min(nowSec, to), to);
            if (end <= start) return null;
            return (
              <div
                key={tw.id}
                title={`Test run: ${tw.title}`}
                style={{
                  position: 'absolute',
                  left: `${pct(start)}%`,
                  width: `${Math.max(pct(end) - pct(start), 0.15)}%`,
                  top: 0,
                  bottom: 0,
                  background:
                    'repeating-linear-gradient(45deg, rgba(84,98,111,0.22) 0 4px, transparent 4px 8px)',
                  pointerEvents: 'none',
                }}
              />
            );
          })}
        </div>

        {/* Event glyph row */}
        <EventMarkerRow
          events={events}
          range={range}
          onSelect={onSelectEvent}
          selectedEventId={selectedEventId}
        />
      </div>
    </div>
  );
}
